import React, { useState } from "react";
import { useTheme } from "@emotion/react";
import {
  faCircleCheck,
  faTimesCircle,
} from "@fortawesome/pro-regular-svg-icons";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import { Tooltip } from "@ternary/api-lib/ui-lib/components/Tooltip";
import { FormField } from "@/ui-lib/components/Form";
import TextInput from "@/ui-lib/components/TextInput";
import copyText from "../copyText";

const S3_REF_PATTERNS = [
  {
    pattern: /^s3:\/\/([^/]+)\/(.*)$/, // S3 URI
    extract: (m: RegExpMatchArray) => [m[1], m[2]],
  },
  {
    pattern: /^arn:aws:s3:::([^/]+)\/(.*)$/, // S3 ARN
    extract: (m: RegExpMatchArray) => [m[1], m[2]],
  },
  {
    pattern:
      /^https:\/\/s3\.console\.aws\.amazon\.com\/s3\/object\/([^?]+)\?.*prefix=([^&]+)/, // S3 console URL
    extract: (m: RegExpMatchArray) => [m[1], m[2]],
  },
  {
    pattern: /^https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.*)$/, // S3 direct URL
    extract: (m: RegExpMatchArray) => [m[1], m[3], m[2]],
  },
  {
    pattern: /^https:\/\/s3\.([^.]+)\.amazonaws\.com\/([^/]+)\/(.*)$/, // S3 direct URL with region in path
    extract: (m: RegExpMatchArray) => [m[2], m[3], m[1]],
  },
];

// Match manifest path format (e.g. /20230101-20230131/20230120T023621Z/cur-Manifest.json)
const MANIFEST_PATH_PATTERN =
  /(\d{8})-(\d{8})\/\d{8}T\d{6}Z\/[^/]+-Manifest\.json$/;
// Match billing period format (e.g. BILLING_PERIOD=2024-09)
const FOCUS_CUR_PATTERN =
  /metadata\/BILLING_PERIOD=\d{4}-\d{2}\/.*[^/]+-Manifest\.json$/;

// Match Azure Blob URL format (e.g. https://${container}.blob.core.windows.net/container/path)
const AZURE_BLOB_PATTERN = /^https:\/\/([^/]+)\/([^/]+)\/(.+)$/;

export type FullyQualifiedResourceType = "BQTable" | "S3Ref" | "AzureBlob";

export const parseFullyQualifiedResourceString = (
  resourceString: string,
  type: FullyQualifiedResourceType
): string[] => {
  try {
    if (type === "BQTable") {
      const parts = resourceString.split(".");
      if (parts.length !== 3 || parts.some((part) => !part.trim())) {
        throw new Error();
      }
      return parts;
    }

    if (type === "S3Ref") {
      for (const { pattern, extract } of S3_REF_PATTERNS) {
        const match = resourceString.match(pattern);
        if (match) {
          const [bucketName, path, region] = extract(match);
          const sanitizedPath = decodeURIComponent(path).replace(/\/+$/, "");

          if (sanitizedPath.match(MANIFEST_PATH_PATTERN)) {
            return [
              bucketName,
              sanitizedPath.replace(/\/(\d{8})-(\d{8})\/.*$/, ""),
              region,
            ].filter(Boolean);
          }

          if (sanitizedPath.match(FOCUS_CUR_PATTERN)) {
            return [
              bucketName,
              sanitizedPath.replace(/\/metadata\/.*$/, ""),
              region,
            ].filter(Boolean);
          }

          return [bucketName, sanitizedPath, region].filter(Boolean);
        }
      }
      throw new Error();
    }

    if (type === "AzureBlob") {
      const match = resourceString.match(AZURE_BLOB_PATTERN);
      if (!match) {
        throw new Error();
      }

      const [, domain, container, path] = match;
      const sanitizedPath = decodeURIComponent(path).replace(/\/+$/, "");

      // Remove date range and everything after it (e.g. /20240501-20240531/...)
      const basePath = sanitizedPath.replace(/\/(\d{8})-(\d{8})\/.*$/, "");

      // Return the full domain as the first element
      return [`https://${domain}`, container, basePath];
    }

    throw new Error("Invalid resource type");
  } catch {
    throw new Error("Invalid resource string");
  }
};

export const FullyQualifiedResourceString = ({
  description = copyText.integrationFullyQualifiedResourceStringDescription,
  label = copyText.integrationFullyQualifiedResourceString,
  onParse,
  type,
}: {
  description?: string;
  label?: string;
  onParse: (parts: string[]) => void;
  type: FullyQualifiedResourceType;
}) => {
  const theme = useTheme();

  const [tableResourceString, setTableResourceString] = useState("");
  const [parsedTableResourceStatus, setParsedTableResourceStatus] = useState<
    "valid" | "invalid"
  >("valid");

  const handleTableResourceStringChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTableResourceString(e.target.value);
    if (!e.target.value) {
      setParsedTableResourceStatus("valid"); // No icon if empty
      return;
    }

    try {
      const resourceParts = parseFullyQualifiedResourceString(
        e.target.value,
        type
      );

      setParsedTableResourceStatus("valid");
      onParse(resourceParts);
    } catch {
      setParsedTableResourceStatus("invalid");
    }
  };

  return (
    <Flex flex="1 1 0" display="flex" alignItems="center" gap={theme.space_xs}>
      <FormField
        caption={description}
        label={label}
        input={TextInput}
        value={tableResourceString}
        onBlur={handleTableResourceStringChange}
        onChange={(e) => setTableResourceString(e.target.value)}
      />

      {/* Add some bottom margin to align the icon because the form field caption stretches the flex space */}
      <Box marginBottom={theme.space_lg}>
        {tableResourceString && parsedTableResourceStatus === "valid" && (
          <Icon
            aria-label="resource-valid"
            icon={faCircleCheck}
            color={theme.feedback_positive}
          />
        )}
        {tableResourceString && parsedTableResourceStatus === "invalid" && (
          <Tooltip content={copyText.integrationInvalidResourceString}>
            <Icon
              aria-label={copyText.integrationInvalidResourceString}
              icon={faTimesCircle}
              color={theme.feedback_negative}
            />
          </Tooltip>
        )}
      </Box>
    </Flex>
  );
};
